/**
 * Live Alpaca account balance hook.
 *
 * Seeds once from GET /api/trade/account on mount, then updates in place
 * every time a TradeEvent carries an account snapshot (broadcast from
 * the server on any Alpaca trade_updates event). No polling; the balance
 * displayed in the UI matches broker truth within ~1s of any fill.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  AlpacaAccountSnapshot,
  TradeEvent,
  useTradeStream,
} from './useTradeStream';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function useLiveAccount(): {
  account: AlpacaAccountSnapshot | null;
  loading: boolean;
} {
  const [account, setAccount] = useState<AlpacaAccountSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  // Seed from REST on mount.
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/trade/account`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.account) setAccount(data.account);
      })
      .catch(() => { /* ignore — WS will eventually update */ })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live updates: any TradeEvent carrying `account` replaces the snapshot.
  const onEvent = useCallback((ev: TradeEvent) => {
    if (ev.account) setAccount(ev.account);
  }, []);
  useTradeStream(onEvent);

  return { account, loading };
}
